import { useField } from "formik";
import { t } from "i18next";
import { DatePicker, MenuOption, TimePicker } from "kubra-ux-forge";
import React, { useMemo } from "react";
import { setTimeToZero } from "../utils";

interface DateTimeFieldProps {
  name: string;
  label: string;
  placeholder?: string;
  editableIfPrefilled?: boolean;
  min?: string;
  max?: string;
}

export const DateTimeField: React.FC<DateTimeFieldProps> = ({
  name,
  label,
  placeholder,
  editableIfPrefilled,
  min,
  max,
}) => {
  const [fieldDate, metaDate, helpersDate] = useField(`${name}.date`);
  const [fieldTime, metaTime, helpersTime] = useField(`${name}.time`);

  // TODO: unintuitive way of setting defaultValue for TimePicker.
  // Need to fix in Forge. It should accept a string, not an object.
  // Memoize the TimePicker defaultValue because it requires defaultValue to be
  // an object of type: { name: "HH:mm" }. If we don't memoize, a new object will be
  // created on each render and TimePicker's useEffect re-runs because defaultValue
  // changes and so it resets the selected value.
  const timePickerDefaultValue = useMemo(
    () => ({ name: metaTime.initialValue }),
    [metaTime.initialValue]
  );

  let disabled = false;

  if (metaDate.initialValue && !editableIfPrefilled) {
    disabled = true;
  }

  const handleDateChange = (selectedDate: Date | Date[]) => {
    if (selectedDate instanceof Date) {
      helpersDate.setValue(selectedDate.toISOString());
    } else if (Array.isArray(selectedDate) && selectedDate.length > 0) {
      helpersDate.setValue(selectedDate[0].toISOString());
    }
  };

  return (
    <div className="date-field-container">
      <div data-cy={`${name}.date-wrapper`} aria-invalid={metaDate.touched && !!metaDate.error}>
        <DatePicker
          {...fieldDate}
          onChange={handleDateChange}
          errorText={metaDate.touched ? metaDate.error : undefined}
          hasError={metaDate.touched ? !!metaDate.error : false}
          label={label}
          value={fieldDate.value ? new Date(fieldDate.value) : undefined}
          placeholder={placeholder}
          dataCy={`${name}.date`}
          disabled={disabled}
          {...(min
            ? {
                minDate: setTimeToZero(new Date(min)),
              }
            : null)}
          {...(max
            ? {
                maxDate: setTimeToZero(new Date(max)),
              }
            : null)}
        />
      </div>

      <div data-cy={`${name}.time-aria-wrapper`} aria-invalid={metaTime.touched && !!metaTime.error}>
        <TimePicker
          {...fieldTime}
          is24HourClock
          label={label}
          onChange={(option: MenuOption | MenuOption[]) => {
            Array.isArray(option) && helpersTime.setValue(option[0].name);
          }}
          // Avoid specifying defaultValue prop if it's undefined.
          // Otherwise we get the controlled to uncontrolled React error.
          {...(timePickerDefaultValue.name
            ? { defaultValue: timePickerDefaultValue }
            : null)}
          {...(placeholder ? { placeholder: t(placeholder) } : null)}
          errorText={metaTime.touched ? metaTime.error : undefined}
          hasError={metaTime.touched ? !!metaTime.error : false}
          dataCy={`${name}.time`}
        />
      </div>
    </div>
  );
};
